import React, {type FormEvent, useState} from "react";
import type ConstructionNewAddTagsDialogProps from "./ConstructionNewAddTagsDialogProps";
import IsaacDialog from "../../../../components/isaacDialog/IsaacDialog";
import {
  ComboBox,
  DefaultButton,
  DialogFooter,
  DialogType,
  PrimaryButton  
} from "@fluentui/react";
import type {IComboBox, IComboBoxOption} from "@fluentui/react";
import type ApiResponse from "../../../../services/api/types/ApiResponse";
import {useDispatch, useSelector} from "react-redux";
import type {RootState} from "../../../../store";
import useConstructionNewTagHook from "./hooks/useConstructionNewTagHook";
import {addTags} from "../../constructionNewSlice";
import type TagDto from "../../../../dto/construction/TagDto";

const ConstructionNewAddTagsDialog: React.FunctionComponent<ConstructionNewAddTagsDialogProps> = ({
  isHidden,
  handleDismiss,
}) => {
  const {data} = useSelector((state: RootState) => state.constructionNew);
  const {tags} = useSelector((state: RootState) => state.constructionsNew);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<ApiResponse<TagDto[]>>();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [currentTags, setCurrentTags] = useState<IComboBoxOption[]>(tags);
  const {addTagsToConstruction} = useConstructionNewTagHook();
  const dispatch = useDispatch();

  const handleChange = (
    event: FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
    index?: number | undefined,
    value?: string | undefined,
  ): void => {
    if (option !== undefined) {
      setCurrentTags((preState: IComboBoxOption[]) => [
        ...preState.map(el => (el.key === option.key ? {...el, selected: option.selected} : el)),
      ]);
      setSelectedTags(prevState =>
        option.selected === true
          ? [...prevState?.filter(o => o !== option.text), option.text]
          : [...prevState?.filter(o => o !== option.text)],
      );
      return;
    }
    if (value !== undefined) {
      setCurrentTags((preState: IComboBoxOption[]) => [...preState, {key: value, text: value, selected: true}]);
      setSelectedTags(prevState => [...prevState, value]);
    }
  };

  const handleAddTags = async (): Promise<void> => {
    setCurrentTags((preState: IComboBoxOption[]) => [...preState.map(el => ({...el, selected: false}))]);
    setIsLoading(true);
    if (data) {
      const tagsToAdd = selectedTags.map(t => ({value: t}));
      const response = await addTagsToConstruction(
        data.id,
        tagsToAdd,
      );
      setResult(response);
      dispatch(addTags(tagsToAdd));
    }
    setSelectedTags([]);
    handleDismiss();
    setIsLoading(false);
  };

  const handleDismissAndClearState = (): void => {
      setCurrentTags((preState: IComboBoxOption[]) => [
        ...preState.map(el => ({...el, selected: false})),
      ]);
  
      setSelectedTags([]);
      handleDismiss();
    };

  return (
    <IsaacDialog
      isHidden={isHidden}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Add tag to the construction",
      }}
      modalProps={{
        titleAriaId: "tag",
        subtitleAriaId: "mySubTextId",
        isBlocking: true,
        containerClassName: "ms-dialogMainOverride ",
      }}
      isLoading={isLoading}
      errorMessage={result?.errors}
      successMessage="Calculation created successfully"
      handleDialogDismiss={handleDismiss}>
      <>
        <ComboBox
          label="Add a new tag or select from existing tags."
          allowFreeform={true}
          multiSelect
          selectedKey={selectedTags}
          options={currentTags}
          onChange={handleChange}
        />
        <DialogFooter>
          <PrimaryButton
            onClick={handleAddTags}
            text="Add"
            disabled={selectedTags !== undefined && selectedTags.length <= 0}
          />
          <DefaultButton onClick={handleDismissAndClearState} text="Cancel" />
        </DialogFooter>
      </>
    </IsaacDialog>
  );
};
export default ConstructionNewAddTagsDialog;
